import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { BRANCHES, maskPhone, formatIST } from "@/lib/branches";
import { KonattuLogo } from "@/lib/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function HistoryPage() {
  const [branch, setBranch] = useState("ALL");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch && branch !== "ALL") params.branch = branch;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await apiClient.get("/winners/history", { params });
      setRows(data.winners || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const header = ["Date", "Branch", "Winner Name", "Phone"];
    const lines = [header.join(",")];
    rows.forEach(r => {
      lines.push([r.draw_date, `"${r.branch}"`, `"${r.name.replace(/"/g, '""')}"`, r.phone].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `konattu-draw-history-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click(); a.remove();
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <KonattuLogo className="h-10" showTagline={false} />
            <span className="kg-badge-red">Draw History</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/admin" className="text-neutral-700 hover:text-[#CC0000]" data-testid="back-to-admin-link">← Back to Admin</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="kg-card kg-card-red-top p-6">
          <h3 className="font-display text-2xl text-neutral-900">Filters</h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-sm text-neutral-700">Branch</label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="kg-input mt-2 h-11 border-2" data-testid="history-branch-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Branches</SelectItem>
                  {BRANCHES.map((b, i) => (
                    <SelectItem key={i} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-neutral-700">Start date</label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="kg-input mt-2 h-11 border-2" data-testid="history-start-date" />
            </div>
            <div>
              <label className="text-sm text-neutral-700">End date</label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="kg-input mt-2 h-11 border-2" data-testid="history-end-date" />
            </div>
            <div className="flex gap-2">
              <Button onClick={load} className="kg-btn-red h-11 flex-1" data-testid="history-apply-btn">Apply</Button>
              <Button variant="outline" onClick={exportCSV} className="h-11 border-[#CC0000] text-[#CC0000] hover:bg-[#FFE9E9]" data-testid="history-export-btn">Export CSV</Button>
            </div>
          </div>
        </div>

        <div className="kg-card mt-6 p-6">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-xl text-neutral-900">Results</h4>
            <span className="text-sm text-neutral-500">{rows.length} winners</span>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-200">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Branch</th>
                  <th className="py-2 pr-4">Winner</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Drawn at</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="py-6 text-center text-neutral-500">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-neutral-500" data-testid="history-empty">No winners found for the selected filters.</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-neutral-100" data-testid={`history-row-${i}`}>
                    <td className="py-2 pr-4">{r.draw_date}</td>
                    <td className="py-2 pr-4">{r.branch}</td>
                    <td className="py-2 pr-4 font-medium">{r.name}</td>
                    <td className="py-2 pr-4 font-mono">{maskPhone(r.phone)}</td>
                    <td className="py-2 pr-4 text-neutral-500">{formatIST(r.drawn_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
